/**
 * Options to create bundles from the bundler plugin.
 */
export interface BundleOptions {
  /**
   * The absolute input path for the entry points.
   */
  inputPath: string;

  /**
   * The entry point paths map. Key is path to the output file, relative to `outputPath` and stripped of `.js`
   * extension. Value is the input entry path, relative to `inputPath` including the extension. For example:
   * `{ 'lib/index': 'src/index.js' }`
   *
   * Note: Non-javascript extensions, such as `.css` or `.scss`, will not be stripped from the key.
   * This is to ensure the bundler keeps the original extension intact and adds `.js` after it.
   */
  entries: Record<string, string>;

  /**
   * The output path for bundled files. This can be either relative to `inputPath`, or absolute.
   * Defaults to `inputPath`.
   */
  outputPath?: string;

  /**
   * List of dependencies which should be externalized. Only applicable for ESM output.
   */
  external?: string[];

  /**
   * List of dependencies which should be inlined.
   */
  inlined?: string[];

  /**
   * If true, minify the output.
   */
  minify?: boolean;

  /**
   * If specified, will generate sourcemaps for the output. This may have a tax on performance.
   */
  sourcemap?: 'none' | 'linked' | 'inline' | 'external' | 'inline-and-external';

  /**
   * If true, the bundler is expected to stay alive and take requests to rebuild on demand. File watching is
   * managed at a higher layer. When we no longer care about incremental bundling, the bundler will receive a call
   * to `dispose` if returned.
   */
  incremental?: boolean;

  /**
   * If specified, will use a specific bundler type. This is not recommended unless there is a clear reason to hardcode
   * the bundler type.
   */
  // TODO: rename either this or BundleResult.bundlerName for consistency
  bundlerType?: string;

  /**
   * If specific settings are required to pass through to the specific bundler, they can be specified here are are
   * expected to be merged in by the plugin. Specifying bundler-specific overrides is not recommended, as it locks
   * us into a specific bundler implementation.
   */
  overrideOptions?: Record<string, unknown>;
}
